import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, renderSlot as _renderSlot, withCtx as _withCtx, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-71bf75f0"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "inner" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { class: "lastUpdatedWrapper" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { key: 1 }

import { formatInTimeZone } from 'date-fns-tz';
import { enGB } from 'date-fns/locale';
import {
  onMounted,
  ref,
  watch,
  watchEffect,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { INFO_CARD_COLOR } from '@constants/info-card-color';
import { timezone, routeInformationUrl, timetableResultsUrl } from '../../envVariables';
import {
  RouteDetail,
  Icon,
  mapRoutes,
  mapStatusToRteContent,
  parseRouteStatus,
  sortRouteSailingStatusByPriority,
  sortRouteServiceStatusTypeByPriority,
  RouteServiceStatus,
  RteContentType,
  ServiceStatusType,
  InfoCard,
} from '../../index';
import { useServiceStatusStore } from '../../stores';
import {
  Route, RouteStatus, StatusCategory, SubStatus,
} from '../../types/api';
import { ServiceStatusTabs } from '../ServiceStatusTabs';


export default /*#__PURE__*/_defineComponent({
  __name: 'ServiceStatusItem',
  props: {
    routeCode: {},
    isMobileAppView: { type: Boolean },
    routeToTimetable: { type: Function }
  },
  emits: ['getRouteCode'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emits = __emit;
const { t } = useI18n();

const serviceStatusStore = useServiceStatusStore();

const route = ref<RouteServiceStatus>();
const statusUpdated = ref<string>();
const tabsLoading = ref<boolean>(false);
const sailingStatusReports = ref<
  Array<{
    status: ServiceStatusType;
    statusHeading: string;
    rteContent?: Array<RteContentType>;
    showAlternativeCta?: boolean;
    showRefundCta?: boolean;
  }>
>([]);
const serviceStatuses = ref<Array<RteContentType>>();
const informationStatuses = ref<Array<RteContentType>>();

const redirectToRouteInfo = () => {
  window.open(`${routeInformationUrl}/${route.value?.routeCode}`);
};

const getStatuses = (statusItem: Route, category: StatusCategory) => statusItem
  .routeStatuses.filter(
    (routeStatus: RouteStatus) => routeStatus.status === category,
  );

const getStatusHeadingBySubStatus = (subStatus: SubStatus) => {
  switch (subStatus) {
    case SubStatus.AllSailingsCancelled:
      return t('Cancelled for the remainder of today');
    case SubStatus.BeAware:
      return t('Be aware / At risk');
    case SubStatus.Disruptions:
      return t('One or more sailings are disrupted');
    default:
      return t('Normal service');
  }
};

const renderServiceStatusItem = (serviceStatusItem: Route) => {
  const routeSailingStatuses = serviceStatusItem?.routeStatuses.filter(
    (routeStatus: RouteStatus) => routeStatus.status === StatusCategory.Sailing,
  );

  if (routeSailingStatuses.length > 0) {
    const routeSailingStatus = routeSailingStatuses.sort(
      sortRouteSailingStatusByPriority,
    );

    if (routeSailingStatus[0]?.updatedAtDateTime) {
      statusUpdated.value = formatInTimeZone(
        Date.parse(routeSailingStatus[0]?.updatedAtDateTime),
        timezone,
        'dd MMM yyyy',
        { locale: enGB },
      );
    }

    sailingStatusReports.value = routeSailingStatus
      .map((item) => {
        const content = mapStatusToRteContent(item);

        const showCta = !props.isMobileAppView && (item.subStatus === SubStatus.AllSailingsCancelled
            || item.subStatus === SubStatus.Disruptions);

        return {
          status: parseRouteStatus(item.subStatus),
          statusHeading: getStatusHeadingBySubStatus(item.subStatus),
          rteContent: [content],
          showAlternativeCta: showCta && route.value && route.value.ports.length <= 2,
          showRefundCta: showCta,
        };
      })
      .sort(sortRouteServiceStatusTypeByPriority);
  } else {
    sailingStatusReports.value = [];
  }

  sailingStatusReports.value = sailingStatusReports.value.length > 0
    ? sailingStatusReports.value
    : [
      {
        status: ServiceStatusType.Normal,
        statusHeading: t('Normal service'),
      },
    ];

  const routeServiceStatuses = getStatuses(
    serviceStatusItem,
    StatusCategory.Service,
  );

  if (routeServiceStatuses.length > 0) {
    serviceStatuses.value = routeServiceStatuses.map((item) => mapStatusToRteContent(item));
  } else {
    serviceStatuses.value = [];
  }

  const routeInformationStatuses = getStatuses(
    serviceStatusItem,
    StatusCategory.Information,
  );

  if (routeInformationStatuses.length > 0) {
    informationStatuses.value = routeInformationStatuses.map((item) => mapStatusToRteContent(item));
  } else {
    informationStatuses.value = [];
  }
};

const populateView = async () => {
  const selectedRoute = serviceStatusStore.serviceStatus?.filter(
    (item) => item.routeCode === props.routeCode,
  )[0] as Route;

  const mappedRoute = [selectedRoute].map((item) => mapRoutes(item))[0];
  route.value = mappedRoute;

  if (serviceStatusStore.routeServiceStatus?.routeCode === props.routeCode) {
    renderServiceStatusItem(serviceStatusStore.routeServiceStatus);
    tabsLoading.value = false;
  } else {
    await serviceStatusStore.getRouteServiceStatus(props.routeCode);
  }

  emits('getRouteCode', selectedRoute.routeCode);
};

onMounted(async () => {
  // Setup
  await serviceStatusStore.getServiceStatus();
});

watchEffect(() => {
  if (serviceStatusStore.serviceStatus) {
    populateView();
  }
});

watch(
  () => serviceStatusStore.isServiceStatusLoading,
  (newVal) => {
    tabsLoading.value = newVal;
  },
);

watch(
  () => serviceStatusStore.routeServiceStatus,
  (newRoute) => {
    if (
      newRoute
    ) {
      renderServiceStatusItem(newRoute);
    }
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        (!_ctx.isMobileAppView)
          ? (_openBlock(), _createElementBlock("h2", _hoisted_3, _toDisplayString(_ctx.$t("Route service status")), 1))
          : _createCommentVNode("", true),
        (_unref(serviceStatusStore).isServiceStatusError)
          ? (_openBlock(), _createBlock(_unref(InfoCard), {
              key: 1,
              "icon-name": "warning",
              text: _ctx.$t('Trouble retrieving service'),
              lg: true,
              "background-color": _unref(INFO_CARD_COLOR).white,
              "border-color": _unref(INFO_CARD_COLOR).red
            }, null, 8, ["text", "background-color", "border-color"]))
          : _createCommentVNode("", true),
        _createVNode(_unref(RouteDetail), {
          status: route.value?.status,
          ports: route.value?.ports,
          "destination-name": route.value?.destinationName
        }, {
          lastUpdated: _withCtx(() => [
            _createElementVNode("div", _hoisted_4, [
              (statusUpdated.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(_ctx.$t("Last updated text and time", [statusUpdated.value])), 1))
                : (_openBlock(), _createElementBlock("p", _hoisted_6, "   ")),
              (route.value?.ports?.length < 3 && !route.value?.isFreight && !route.value?.hideOnMap)
                ? (_openBlock(), _createElementBlock("a", {
                    key: 2,
                    onClick: () => _ctx.routeToTimetable(route.value),
                    class: "link"
                  }, [
                    _createTextVNode(_toDisplayString(_ctx.$t("View timetable")) + " ", 1),
                    _createVNode(_unref(Icon), { "icon-name": "redLink" })
                  ], 8, _hoisted_7))
                : _createCommentVNode("", true)
            ])
          ]),
          favourite: _withCtx(() => [
            _renderSlot(_ctx.$slots, "favourite")
          ]),
          infoCard: _withCtx(() => [
            _renderSlot(_ctx.$slots, "infoCard")
          ]),
          _: 3
        }, 8, ["status", "ports", "destination-name"])
      ])
    ]),
    (!tabsLoading.value)
      ? (_openBlock(), _createBlock(_unref(ServiceStatusTabs), {
          key: 0,
          "sailing-status-reports": sailingStatusReports.value,
          "service-statuses": serviceStatuses.value,
          "information-statuses": informationStatuses.value,
          "on-find-alternate-click": () => redirectToRouteInfo()
        }, {
          liveUpdatesInfoCard: _withCtx(() => [
            _renderSlot(_ctx.$slots, "liveUpdatesInfoCard")
          ]),
          _: 3
        }, 8, ["sailing-status-reports", "service-statuses", "information-statuses", "on-find-alternate-click"]))
      : _createCommentVNode("", true),
    (tabsLoading.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_8, " Loading... "))
      : _createCommentVNode("", true)
  ], 64))
}
}

})